import type { Block } from '@news-mono/web-common'
import React from 'react'
import {
    ArticleTable,
    ArticleTableCaption,
    ArticleTableCell,
    ArticleTableHeaderCell,
    ArticleTableWrapper,
} from './ArticleBlockContent.styled'

type TableBlock = Extract<Block, { kind: 'table' }>

export function renderTableBlock({
    block,
    renderCellBlock,
}: {
    block: TableBlock
    renderCellBlock: (
        cellBlock: Block,
        context: {
            section: 'head' | 'body' | 'foot'
            rowIndex: number
            cellIndex: number
            blockIndex: number
        },
    ) => React.ReactNode
}) {
    const headRows = block.rows.filter((row) => row.rowGroup === 'head')
    const bodyRows = block.rows.filter((row) => row.rowGroup === 'body')
    const footRows = block.rows.filter((row) => row.rowGroup === 'foot')

    const renderRows = (
        rows: typeof block.rows,
        section: 'head' | 'body' | 'foot',
    ) =>
        rows.map((row, rowIndex) => (
            <tr key={`table-${section}-row-${rowIndex}`}>
                {row.cells.map((cell, cellIndex) => {
                    const CellComponent =
                        cell.cellType === 'head'
                            ? ArticleTableHeaderCell
                            : ArticleTableCell

                    return (
                        <CellComponent
                            key={`table-${section}-cell-${rowIndex}-${cellIndex}`}
                            colSpan={cell.colSpan}
                            style={{
                                textAlign: cell.horizontalAlign,
                                verticalAlign: cell.verticalAlign,
                            }}
                        >
                            {cell.blocks.map((cellBlock, blockIndex) => (
                                <React.Fragment
                                    key={`table-${section}-cell-block-${rowIndex}-${cellIndex}-${blockIndex}`}
                                >
                                    {renderCellBlock(cellBlock, {
                                        section,
                                        rowIndex,
                                        cellIndex,
                                        blockIndex,
                                    })}
                                </React.Fragment>
                            ))}
                        </CellComponent>
                    )
                })}
            </tr>
        ))

    return (
        <ArticleTableWrapper
            tabIndex={0}
            role="region"
            aria-label={
                block.caption ? 'Table: ' + block.caption : 'Scrollable table'
            }
        >
            <ArticleTable>
                {block.caption && (
                    <ArticleTableCaption>{block.caption}</ArticleTableCaption>
                )}
                {headRows.length > 0 && (
                    <thead>{renderRows(headRows, 'head')}</thead>
                )}
                {bodyRows.length > 0 && (
                    <tbody>{renderRows(bodyRows, 'body')}</tbody>
                )}
                {footRows.length > 0 && (
                    <tfoot>{renderRows(footRows, 'foot')}</tfoot>
                )}
            </ArticleTable>
        </ArticleTableWrapper>
    )
}
